import { apiService } from './api'
import { ContentPost, PostBrand } from './scoutService'

/**
 * System Dashboard service — cross-tenant analytics for support/system_admin.
 * Hits ath-atlas-be /admin/system-dashboard/* (gated by access:support).
 *
 * NOTE: /tenants is live; the aggregate endpoints (analytics/emv-over-time/emv-by-platform)
 * are being built — callers should tolerate failures gracefully while we iterate.
 */

export interface SystemTenant {
  customerId: string
  name: string
}

export interface SystemDashboardFilters {
  customerId?: string // a single tenant, or omitted for "all active tenants"
  period?: string
  customStartDate?: string
  customEndDate?: string
  platformIds?: string[]
  postTypes?: string[]
}

const BASE = '/admin/system-dashboard'

// A tenant's aggregate stats for one category × format cell (cross-tenant drill-down).
export interface SystemTenantCard {
  customer_id: string
  customer_name: string
  total_posts: number
  total_emv: number
  total_views: number
  total_likes: number
  total_comments: number
  total_shares: number
  followers: number
}

class SystemDashboardService {
  /** Active-tenant list for the filter dropdown. */
  async getTenants(): Promise<{ success: boolean; data: SystemTenant[] }> {
    return apiService.get<{ success: boolean; data: SystemTenant[] }>(`${BASE}/tenants`)
  }

  /**
   * Consolidated dashboard payload (KPI totals + EMV-over-time + EMV-by-platform) in one
   * round-trip. Preferred over the individual endpoints — avoids parallel calls each re-running
   * the heavy merged-view CTE against the remote DB.
   */
  async getOverview(filters: SystemDashboardFilters = {}): Promise<any> {
    return apiService.get<any>(`${BASE}/overview`, this.toParams(filters))
  }

  /** Aggregate KPI/analytics across active tenants (or one selected tenant). */
  async getAnalytics(filters: SystemDashboardFilters = {}): Promise<any> {
    return apiService.get<any>(`${BASE}/analytics`, this.toParams(filters))
  }

  /** EMV over time (aggregate). */
  async getEmvOverTime(filters: SystemDashboardFilters = {}): Promise<any> {
    return apiService.get<any>(`${BASE}/emv-over-time`, this.toParams(filters))
  }

  /** EMV by platform (aggregate). */
  async getEmvByPlatform(filters: SystemDashboardFilters = {}): Promise<any> {
    return apiService.get<any>(`${BASE}/emv-by-platform`, this.toParams(filters))
  }

  /** Content Performance: category × format cells (cross-tenant Brand Scout). */
  async getContentPerformance(filters: { customerId?: string; period?: string; customStartDate?: string; customEndDate?: string } = {}): Promise<{ success: boolean; data: Array<{ content_category: string; post_type: string; post_count: number; total_emv: number }> }> {
    return apiService.get(`${BASE}/content-performance`, this.toParams(filters))
  }

  /** Posts within a category × format (cross-tenant). */
  async getContentPerformancePosts(opts: { customerId?: string; contentCategory: string; format: string; period?: string; customStartDate?: string; customEndDate?: string }): Promise<{ success: boolean; data: ContentPost[] }> {
    return apiService.get(`${BASE}/content-performance/posts`, {
      customerId: opts.customerId || undefined,
      contentCategory: opts.contentCategory,
      format: opts.format,
      period: opts.period || undefined,
      customStartDate: opts.customStartDate || undefined,
      customEndDate: opts.customEndDate || undefined,
    })
  }

  /** Tenants with content in a category × format (cross-tenant drill-down cards). */
  async getContentPerformanceTenants(opts: { customerId?: string; contentCategory: string; format: string; period?: string; customStartDate?: string; customEndDate?: string }): Promise<{ success: boolean; data: SystemTenantCard[] }> {
    return apiService.get(`${BASE}/content-performance/tenants`, {
      customerId: opts.customerId || undefined,
      contentCategory: opts.contentCategory,
      format: opts.format,
      period: opts.period || undefined,
      customStartDate: opts.customStartDate || undefined,
      customEndDate: opts.customEndDate || undefined,
    })
  }

  /** Brands detected in a single post (needs the post's customerId + postId + partitionKey). */
  async getPostBrands(customerId: string, postId: number, partitionKey: number): Promise<{ success: boolean; data: PostBrand[] }> {
    return apiService.get(`${BASE}/content-performance/post-brands`, { customerId, postId, partitionKey })
  }

  private toParams(f: SystemDashboardFilters): Record<string, any> {
    const p: Record<string, any> = {}
    if (f.customerId) p.customerId = f.customerId
    if (f.period) p.period = f.period
    if (f.customStartDate) p.customStartDate = f.customStartDate
    if (f.customEndDate) p.customEndDate = f.customEndDate
    if (f.platformIds?.length) p.platformIds = f.platformIds.join(',')
    if (f.postTypes?.length) p.postTypes = f.postTypes.join(',')
    return p
  }
}

export const systemDashboardService = new SystemDashboardService()
export default systemDashboardService
